import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schema
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://evesmart.qa';

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (title) {
      updateMetaTag('og:title', title, true);
      updateMetaTag('twitter:title', title);
    }

    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
      updateMetaTag('twitter:image', ogImage);
    }

    // Update canonical URL
    const fullCanonical = canonical || `${siteUrl}${location.pathname}`;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    
    canonicalLink.setAttribute('href', fullCanonical);
    updateMetaTag('og:url', fullCanonical, true);

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add/Update Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      
      scriptTag.textContent = JSON.stringify(schema);
    }
  }, [title, description, keywords, canonical, ogImage, schema, location.pathname, siteUrl]);
};

// Schema.org generators
export const generateOrganizationSchema = (siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'EVE Smart Agricultural Services',
  alternateName: 'إيف سمارت للخدمات الزراعية',
  url: siteUrl,
  logo: 'https://static.readdy.ai/image/439cae8384efc9e7b28c054739e211d1/7857bf78d2c9072a724b80513f3fbc5c.png',
  description: 'Leading technological revolution in Qatar\'s agricultural sector with smart farming, hydroponics, and vertical farming solutions supporting Qatar Vision 2030.',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Doha',
    addressCountry: 'QA',
    addressRegion: 'Doha'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '25.276987',
    longitude: '51.520008'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+974-XXXX-XXXX',
    contactType: 'customer service',
    email: 'evevsmart@gmail.com',
    availableLanguage: ['Arabic', 'English']
  },
  sameAs: [
    'https://linkedin.com',
    'https://instagram.com',
    'https://twitter.com'
  ]
});

export const generateWebPageSchema = (siteUrl: string, path: string, title: string, description: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: title,
  description: description,
  url: `${siteUrl}${path}`,
  inLanguage: ['ar', 'en'],
  isPartOf: {
    '@type': 'WebSite',
    name: 'EVE Smart Agricultural Services',
    url: siteUrl
  },
  about: {
    '@type': 'Thing',
    name: 'Smart Agriculture',
    description: 'Agricultural technology and sustainable farming solutions'
  }
});

export const generateServiceSchema = (siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Agricultural Services',
  provider: {
    '@type': 'Organization',
    name: 'EVE Smart Agricultural Services',
    url: siteUrl
  },
  areaServed: {
    '@type': 'Country',
    name: 'Qatar'
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Agricultural Solutions',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Smart Agriculture',
          description: 'AI-powered automated monitoring systems to optimize productivity and reduce resource consumption'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Vertical & Soilless Farming',
          description: 'Advanced hydroponic and semi-hydroponic systems for year-round high-quality crop production'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Soil & Seasonal Farming',
          description: 'Improving soil quality and exporting national products with the highest global quality standards'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Consulting & Design',
          description: 'Building turnkey farms for individuals and companies with customized solutions'
        }
      }
    ]
  }
});

export const generateFAQSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is smart agriculture?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Smart agriculture uses AI-powered automated monitoring systems to optimize productivity and reduce resource consumption through advanced technology and data analytics.'
      }
    },
    {
      '@type': 'Question',
      name: 'What services does EVE Smart provide?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'EVE Smart provides comprehensive agricultural solutions including smart agriculture systems, vertical and soilless farming, traditional soil farming, and consulting and design services for turnkey farm projects.'
      }
    },
    {
      '@type': 'Question',
      name: 'Where is EVE Smart located?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'EVE Smart is located in Doha, Qatar, in the Industrial Area, serving clients across Qatar with innovative agricultural solutions.'
      }
    }
  ]
});
